import React, { useEffect, useState } from 'react';
import './MacroIssuesTable.css';

function formatClock(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

function typeLabel(type) {
  if (type === 'worker_gap') return 'Worker Gap';
  if (type === 'supply_block') return 'Supply Block';
  return type;
}

/**
 * Macro Issues table — rows from /api/replay_facts/…/macro_issues (D0/D25).
 */
const MacroIssuesTable = ({
  apiBaseUrl,
  replayId,
  player,
  onSeek,
  playerName,
}) => {
  const [state, setState] = useState({
    status: 'idle',
    issues: [],
    supplyBlocksPresent: true,
    intervalsPresent: true,
    error: '',
  });

  useEffect(() => {
    if (!apiBaseUrl || !replayId || !player) {
      setState({
        status: 'empty',
        issues: [],
        supplyBlocksPresent: true,
        intervalsPresent: true,
        error: '',
      });
      return undefined;
    }
    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading', error: '' }));
    const url =
      `${apiBaseUrl}/replay_facts/${encodeURIComponent(replayId)}` +
      `/macro_issues?player=${encodeURIComponent(player)}`;
    fetch(url)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;
        const issues = Array.isArray(data?.issues) ? data.issues : [];
        const supplyBlocksPresent = data?.supply_blocks_present !== false;
        const intervalsPresent = data?.intervals_present !== false;
        let status = 'ready';
        if (!intervalsPresent) status = 'no-intervals';
        else if (!supplyBlocksPresent && issues.length === 0) status = 'no-supply';
        else if (issues.length === 0) status = 'empty';
        setState({
          status,
          issues,
          supplyBlocksPresent,
          intervalsPresent,
          error: '',
        });
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          status: 'error',
          issues: [],
          supplyBlocksPresent: true,
          intervalsPresent: true,
          error: err?.message || 'Request failed',
        });
      });
    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, replayId, player]);

  const title = `Macro Issues${playerName || player ? ` · ${playerName || player}` : ''}`;

  if (state.status === 'loading' || state.status === 'idle') {
    return (
      <div className="macro-issues" data-testid="macro-issues-table">
        <h4>{title}</h4>
        <p className="macro-issues-empty">Loading Macro Issues…</p>
      </div>
    );
  }
  if (state.status === 'error') {
    return (
      <div className="macro-issues" data-testid="macro-issues-table">
        <h4>{title}</h4>
        <p className="macro-issues-empty" data-testid="macro-issues-error">
          Could not load Macro Issues ({state.error}).
        </p>
      </div>
    );
  }
  if (state.status === 'no-intervals') {
    return (
      <div className="macro-issues" data-testid="macro-issues-table">
        <h4>{title}</h4>
        <p className="macro-issues-empty" data-testid="macro-issues-no-intervals">
          Production Intervals are missing from this ParsedGame. Force re-parse
          to derive Worker Gaps.
        </p>
      </div>
    );
  }
  if (state.status === 'no-supply') {
    return (
      <div className="macro-issues" data-testid="macro-issues-table">
        <h4>{title}</h4>
        <p className="macro-issues-empty" data-testid="macro-issues-no-supply">
          Supply Block list is absent from this ParsedGame (not the same as zero
          blocks). Force re-parse if you expect Supply Blocks here.
        </p>
      </div>
    );
  }
  if (state.status === 'empty') {
    return (
      <div className="macro-issues" data-testid="macro-issues-table">
        <h4>{title}</h4>
        <p className="macro-issues-empty">No Worker Gaps or Supply Blocks detected.</p>
      </div>
    );
  }

  return (
    <div className="macro-issues" data-testid="macro-issues-table">
      <h4>{title}</h4>
      {!state.supplyBlocksPresent && (
        <p className="macro-issues-empty" data-testid="macro-issues-supply-absent">
          Supply Blocks absent from this ParsedGame — Worker Gaps only.
        </p>
      )}
      <table className="macro-issues-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Duration</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {state.issues.map((issue, index) => (
            <tr
              key={`${issue.type}-${issue.start_real_time}-${index}`}
              className="macro-issues-row"
              data-testid="macro-issues-row"
              onClick={() => onSeek?.(issue.start_real_time)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSeek?.(issue.start_real_time);
                }
              }}
              tabIndex={0}
              role="button"
            >
              <td>{typeLabel(issue.type)}</td>
              <td>
                <span className="macro-issues-dur">{Math.round(issue.duration)}s</span>{' '}
                <span className="macro-issues-range">
                  ({formatClock(issue.start_real_time)}–{formatClock(issue.end_real_time)})
                </span>
              </td>
              <td>{issue.detail}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="macro-issues-hint">Click a row to seek the Playhead and pause.</p>
    </div>
  );
};

export default MacroIssuesTable;
